import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SavedRunMap } from "@/components/RunMap";
import { ShareDialog } from "@/components/ShareDialog";
import { ElevationChart } from "@/components/ElevationChart";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { fmtDistance, fmtStoredPace, distUnit, fmtElevation } from "@/lib/units";
import { toast } from "sonner";
import { ChevronLeft, Share2, MapPinOff, Trash2, Loader2 } from "lucide-react";

const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const hasRouteData = (run) => {
  const g = run && run.route_geojson;
  return !!(g && Array.isArray(g.coordinates) && g.coordinates.length >= 2);
};

export default function SavedRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get(`/runs/${id}`).then(({ data }) => setRun(data)).finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/runs/${id}`);
      toast.success("Run deleted");
      navigate("/running");
    } catch (e) {
      toast.error("Could not delete run");
      setDeleting(false);
    }
  };

  if (loading) return <AppShell title="Run"><Skeleton className="h-64 w-full rounded-2xl bg-card" /></AppShell>;
  if (!run) return <AppShell title="Run"><p className="text-muted-foreground">Run not found.</p></AppShell>;

  const stats = [
    ["Distance", fmtDistance(run.distance_mi, user)],
    ["Duration", fmtDur(run.duration_sec)],
    ["Avg pace", fmtStoredPace(run.avg_pace, user)],
    ["Calories", run.calories],
    ["Elevation", fmtElevation(run.elevation_gain_ft, user)],
    ["Splits", (run.splits || []).length],
  ];

  const shareText = `Just logged ${run.title ? `"${run.title}"` : "a run"} on Real Deal Fitness 🏃\n${fmtDistance(run.distance_mi, user)} · ${fmtDur(run.duration_sec)} · ${fmtStoredPace(run.avg_pace, user)} pace 💪`;

  return (
    <AppShell title={run.title || "Run details"}>
      <button onClick={() => navigate(-1)} className="flex items-center text-muted-foreground text-sm mb-3"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="space-y-4">
        {hasRouteData(run) ? (
          <SavedRunMap routeGeojson={run.route_geojson} />
        ) : (
          <Card data-testid="run-no-route" className="rounded-2xl bg-card border-border h-64 flex flex-col items-center justify-center text-center px-6">
            <div className="h-12 w-12 rounded-2xl bg-card border border-border flex items-center justify-center mb-3">
              <MapPinOff className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="font-semibold text-sm">No GPS route for this run</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-[240px]">This run was logged without a tracked route, so there's no map line to show. Your stats below are still saved.</p>
          </Card>
        )}
        <div className="grid grid-cols-3 gap-3">
          {stats.map(([l, v]) => (
            <Card key={l} className="rounded-2xl bg-card border-border p-3 text-center">
              <p className="font-mono text-xl font-bold">{v}</p>
              <p className="text-[11px] text-muted-foreground">{l}</p>
            </Card>
          ))}
        </div>

        <ElevationChart profile={run.elevation_profile} />

        <Button data-testid="share-run-button" onClick={() => setShareOpen(true)} className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold">
          <Share2 className="h-5 w-5 mr-2" /> Share this run
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid="delete-run-button" disabled={deleting} className="w-full h-12 bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/20 font-semibold disabled:opacity-60">
              {deleting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Deleting…</> : <><Trash2 className="h-5 w-5 mr-2" /> Delete run</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-popover border-border" data-testid="delete-run-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Delete this run?</AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">
                This permanently removes the run along with its route and stats. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-run-cancel" className="bg-secondary border-border text-foreground hover:bg-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-run-confirm" onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete run</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {(run.splits || []).length > 0 && (
          <Card className="rounded-2xl bg-card border-border p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Splits</p>
            {run.splits.map((s, i) => (
              <div key={i} className="flex justify-between border-b border-border py-1.5 text-sm">
                <span className="text-muted-foreground">MILE {s.mi ?? s.km}</span>
                <span className="font-mono">{s.pace}/mi</span>
              </div>
            ))}
          </Card>
        )}
      </div>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} defaultText={shareText} title="Share your run" />
    </AppShell>
  );
}
